import { createContext, useContext, useEffect, useCallback, ReactNode } from 'react';
import { THEMES } from '../constants/themes';

type ThemeMode = 'light' | 'dark';

// ThemeContext value interface
interface ThemeContextValue {
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
}

// Dark mode is not shipped in this version (docs/design/simkura-design.md —
// "No dark mode in this version"). The app is light-only and ignores any
// value a previous build persisted. The context shape, the darkTheme stub,
// and the html-class plumbing are kept so dark mode can be reintroduced once
// its tokens are mapped to the design spec.
export function ThemeProvider({ children }: ThemeProviderProps) {
  const mode: ThemeMode = 'light';

  const isDark = mode === 'dark';
  const themeClass = isDark ? THEMES.DARK : THEMES.LIGHT;

  useEffect(() => {
    const root = document.documentElement;
    Object.values(THEMES).forEach((t) => root.classList.remove(t));
    root.classList.add(themeClass);
  }, [themeClass]);

  // No-op while the app is light-only; kept so consumers of the context API
  // (e.g. a future settings toggle) don't need to change.
  const toggleTheme = useCallback(() => {}, []);

  const value: ThemeContextValue = { isDark, toggleTheme };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
